import type { WizardForm } from '@/types/wizard-forms'
import type { PublicVenue, RacePricing } from '@/types/events'
import type { Locale } from '@/i18n'

export type PriceBreakdownItemType = 'base_price' | 'option' | 'voucher_discount'

export interface PriceBreakdownItem {
  type: PriceBreakdownItemType
  label?: string
  field_label?: string
  option_label?: string
  wizard_field_id?: number
  wizard_field_option_id?: number
  code?: string
  race_price_tier_id?: number
  tier_name?: string
  amount: string
}

export interface RegistrationEvent {
  id: number
  name: string
  slug: string
  logo_url: string | null
  website_url: string | null
}

export interface RegistrationEdition {
  id: number
  name: string
  start_date: string
  end_date: string
  venue: PublicVenue | null
  race_rules: string | null
  terms_and_conditions: string | null
}

export interface RegistrationRace {
  id: number
  name: string
  slug: string
  type: string
  base_price: string
  pricing: RacePricing
  distance: string
  elevation: number | null
  // Resolved by the backend (race venue, else edition venue, else null).
  start_venue: PublicVenue | null
  start_venue_inherited: boolean
  start_time: string
  max_participants: number | null
  registrations_open: boolean
  registrations_closing_time: string | null
  is_closed: boolean
  // Absent on older API versions; 'not_yet_open' = paid race awaiting payout setup.
  registration_availability?: 'open' | 'not_yet_open' | 'closed'
  is_waiting_list_active: boolean
  waiting_list_count: number
}

export interface RegistrationOrganiser {
  slug: string | null
  name: string | null
  email: string | null
  website: string | null
  logo_url: string | null
}

export interface RegistrationFormData {
  has_active_vouchers: boolean
  event: RegistrationEvent
  edition: RegistrationEdition
  race: RegistrationRace
  wizard_form: WizardForm | null
  organiser: RegistrationOrganiser
}

export const RegistrationStatus = {
  PaymentPending: 'payment_pending',
  Completed:      'completed',
  Cancelled:      'cancelled',
  WaitingList:    'waiting_list',
  PaymentExpired: 'payment_expired',
} as const

export type RegistrationStatus = typeof RegistrationStatus[keyof typeof RegistrationStatus]

export interface RegistrationFieldAnswer {
  id: number
  wizard_field_id: number
  value: string | null
  selected_options: number[] | null
}

export interface Registration {
  id: number
  user_id: number
  race_id: number
  status: RegistrationStatus
  base_price: string
  options_total: string
  voucher_discount: string
  processing_fee: string
  total_amount: string
  price_breakdown: PriceBreakdownItem[]
  field_answers: RegistrationFieldAnswer[]
  confirmation_token: string
  paid_at: string | null
  waiting_list_position: number | null
  promoted_at: string | null
  promotion_expires_at: string | null
  created_at: string
  updated_at: string
}

export interface PaymentIntentResponse {
  client_secret: string
  stripe_publishable_key: string
  stripe_account_id: string
}

export interface ConfirmationRace {
  name: string
  slug: string
  type: string
  distance: string
  start_time: string | null
  // Resolved by the backend (race venue, else edition venue, else null).
  start_venue: PublicVenue | null
  start_venue_inherited: boolean
}

export interface ConfirmationEvent {
  name: string
  slug: string
  logo_url: string | null
  website_url: string | null
}

export interface ConfirmationEdition {
  name: string
  start_date: string | null
  venue: PublicVenue | null
}

export interface ConfirmationParticipant {
  first_name: string
  last_name: string
  email: string
}

export interface ConfirmationData {
  status: RegistrationStatus
  confirmation_token: string
  base_price: string
  options_total: string
  voucher_discount: string
  total_amount: string
  price_breakdown: PriceBreakdownItem[]
  paid_at: string | null
  waiting_list_position: number | null
  promoted_at: string | null
  promotion_expires_at: string | null
  race: ConfirmationRace
  event: ConfirmationEvent
  edition: ConfirmationEdition
  participant: ConfirmationParticipant
  field_answers: RegistrationFieldAnswer[]
  organiser: {
    slug: string | null
  }
}

export interface FieldAnswer {
  wizard_field_id: number
  value: string | null
  selected_options?: number[]
}

export interface ParticipantRegisterPayload {
  first_name: string
  last_name: string
  email: string
  password: string
  password_confirmation: string
  birth_date: string
  phone: string
  gender: string
  nationality: string
  preferred_language: Locale
}

export interface ParticipantLoginPayload {
  email: string
  password: string
}

export interface StoreRegistrationPayload {
  race_id: number
  field_answers: FieldAnswer[]
  voucher_code?: string | null
}

export interface UpdateParticipantProfilePayload {
  first_name: string
  last_name: string
  birth_date: string
  phone: string
  gender: string
  nationality: string
}
